import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { 
  Monitor, 
  Shield, 
  Activity, 
  Lock, 
  Eye, 
  Zap, 
  CheckCircle, 
  AlertTriangle,
  Server,
  FileText,
  BarChart3,
  Globe,
  Users,
  Clock,
  Bell,
  ArrowRight,
  Github,
  ChevronDown,
  Menu,
  X,
  Cpu,
  HardDrive,
  Network,
  Settings,
  Terminal,
  Play
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export default function AuthPage() {
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  // Get returnTo parameter from URL
  const searchParams = new URLSearchParams(window.location.search);
  const returnTo = searchParams.get("returnTo") || "/dashboard";

  useEffect(() => {
    if (user) {
      navigate(returnTo);
    }
  }, [user, navigate, returnTo]);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    const { error } = await signIn(email, password);
    
    if (error) {
      setError(error.message);
    } else {
      setIsLoginOpen(false);
      navigate(returnTo);
    }
    
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const tenantName = formData.get("tenantName") as string;
    const fullName = formData.get("fullName") as string;

    const { error } = await signUp(email, password, tenantName, fullName);
    
    if (error) {
      setError(error.message);
    } else {
      setIsSignUpOpen(false);
      navigate(returnTo);
    }
    
    setLoading(false);
  };

  const features = [
    { icon: Shield, title: "Secure", subtitle: "Server Monitoring", active: true },
    { icon: Zap, title: "Zero-Config", subtitle: "Agent Deployment" },
    { icon: Lock, title: "Device Token", subtitle: "Authentication" },
    { icon: Activity, title: "Real-time", subtitle: "Performance Metrics" },
    { icon: Server, title: "Centralized", subtitle: "Server Management" },
    { icon: FileText, title: "Unified", subtitle: "Windows & Linux Logs" },
  ];

  const featureSections = [
    {
      id: "security",
      title: "Secure Server Monitoring",
      subtitle: "Enterprise-grade security with zero-trust architecture for Windows and Linux",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80",
      items: [
        { icon: Users, text: "Monitor unlimited Windows & Linux servers with multi-tenant isolation" },
        { icon: Shield, text: "HMAC-SHA256 signed payloads with replay protection" },
        { icon: Lock, text: "Device token authentication - no API keys in agents" },
        { icon: Eye, text: "Privacy mode for GDPR-compliant data collection" },
      ]
    },
    {
      id: "deployment",
      title: "Cross-Platform Agent Deployment",
      subtitle: "Deploy monitoring agents in minutes on Windows and Linux servers",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
      items: [
        { icon: Clock, text: "Install and configure agents in under 5 minutes" },
        { icon: Terminal, text: "PowerShell agent for Windows, Python agent for Linux" },
        { icon: Settings, text: "Secure config with NTFS ACL and file permission protection" },
        { icon: Network, text: "Automatic server registration with enrollment tokens" },
      ]
    },
    {
      id: "metrics",
      title: "Real-time Performance Metrics",
      subtitle: "Monitor CPU, memory, disk, and network with per-second granularity",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
      items: [
        { icon: Cpu, text: "CPU usage, queue length, and per-core metrics" },
        { icon: HardDrive, text: "Disk I/O, SMART health, and volume monitoring" },
        { icon: Activity, text: "Memory utilization and page file statistics" },
        { icon: BarChart3, text: "Historical trends with customizable time ranges" },
      ]
    },
    {
      id: "logs",
      title: "Unified Log Management",
      subtitle: "Centralized collection and analysis of Windows and Linux logs in one view",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80",
      items: [
        { icon: FileText, text: "Windows Security, System, Application, and PowerShell logs" },
        { icon: Terminal, text: "Linux syslog, journald, auth, and kernel logs" },
        { icon: AlertTriangle, text: "Failed login detection and account lockout alerts" },
        { icon: Eye, text: "Filter by server, OS, severity, and search across all logs" },
      ]
    },
    {
      id: "compliance",
      title: "Compliance & Frameworks",
      subtitle: "Track security compliance with built-in framework support and evidence management",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
      items: [
        { icon: Shield, text: "Built-in compliance framework tracking and assessments" },
        { icon: FileText, text: "Evidence collection with automated validation" },
        { icon: CheckCircle, text: "Vulnerability scanners and security findings" },
        { icon: Users, text: "Questionnaires for vendor risk assessments" },
      ]
    },
    {
      id: "management",
      title: "Centralized Server Management",
      subtitle: "Control all your infrastructure from a single dashboard",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
      items: [
        { icon: Server, text: "Manage Windows and Linux servers from one view" },
        { icon: Globe, text: "Multi-tenant architecture for MSPs and enterprises" },
        { icon: Users, text: "Role-based access control with audit logging" },
        { icon: Settings, text: "Task management and REST API integration" },
      ]
    },
    {
      id: "alerts",
      title: "Smart Alerting System",
      subtitle: "Never miss a critical event with intelligent notifications",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80",
      items: [
        { icon: Bell, text: "Email, Slack, and Microsoft Teams notifications" },
        { icon: Activity, text: "Anomaly detection for unusual patterns" },
        { icon: Clock, text: "Configurable alert schedules and escalation" },
        { icon: CheckCircle, text: "Alert acknowledgment and incident tracking" },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)] text-white">
      {/* Announcement Banner */}
      <div className="bg-primary/20 border-b border-primary/30 py-2 px-4">
        <div className="container mx-auto flex items-center justify-center gap-2 text-sm">
          <Badge className="bg-primary text-primary-foreground text-xs">NEW</Badge>
          <span className="text-foreground/80">ServSpect Agent v4.8.0 Released - Token-free Authentication</span>
          <Link to="/knowledge-hub" className="text-primary hover:underline inline-flex items-center gap-1">
            Read Release Article <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Navigation Header */}
      <header className="border-b border-white/10 sticky top-0 z-50 bg-[hsl(224,71%,4%)]/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-3">
                <div className="p-2 bg-primary rounded-lg">
                  <Shield className="w-6 h-6 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold">ServSpect</span>
              </Link>
              
              {/* GitHub Badge */}
              <a 
                href="https://github.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 transition-colors text-sm"
              >
                <Github className="w-4 h-4" />
                <span>OPEN SOURCE</span>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-white hover:bg-white/10 hover:text-white">
                      Features
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="grid gap-3 p-4 w-[400px] bg-[hsl(224,71%,6%)] border border-white/10">
                        <NavigationMenuLink asChild>
                          <a 
                            href="#metrics" 
                            className="block p-3 rounded-lg hover:bg-white/5 cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById('metrics')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                          >
                            <div className="font-medium mb-1">Performance Monitoring</div>
                            <p className="text-sm text-muted-foreground">Real-time CPU, memory, disk, and network metrics</p>
                          </a>
                        </NavigationMenuLink>
                         <NavigationMenuLink asChild>
                          <a 
                            href="#logs" 
                            className="block p-3 rounded-lg hover:bg-white/5 cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById('logs')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                          >
                            <div className="font-medium mb-1">Unified Log Management</div>
                            <p className="text-sm text-muted-foreground">Windows & Linux log collection and analysis</p>
                          </a>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <a 
                            href="#compliance" 
                            className="block p-3 rounded-lg hover:bg-white/5 cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById('compliance')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                          >
                            <div className="font-medium mb-1">Compliance & Frameworks</div>
                            <p className="text-sm text-muted-foreground">Compliance framework tracking and assessments</p>
                          </a>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <a 
                            href="#alerts" 
                            className="block p-3 rounded-lg hover:bg-white/5 cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById('alerts')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                          >
                            <div className="font-medium mb-1">Smart Alerting</div>
                            <p className="text-sm text-muted-foreground">Intelligent notifications via email and Slack</p>
                          </a>
                        </NavigationMenuLink>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                  
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-white hover:bg-white/10 hover:text-white">
                      Resources
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="grid gap-3 p-4 w-[400px] bg-[hsl(224,71%,6%)] border border-white/10">
                        <NavigationMenuLink asChild>
                          <Link to="/install" className="block p-3 rounded-lg hover:bg-white/5">
                            <div className="font-medium mb-1">Installation Guide</div>
                            <p className="text-sm text-muted-foreground">Step-by-step agent deployment instructions</p>
                          </Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <Link to="/privacy" className="block p-3 rounded-lg hover:bg-white/5">
                            <div className="font-medium mb-1">Privacy Policy</div>
                            <p className="text-sm text-muted-foreground">How we protect your data and privacy</p>
                          </Link>
                        </NavigationMenuLink>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link to="/install" className="px-4 py-2 text-sm hover:bg-white/10 rounded-md transition-colors inline-block">
                      Install
                    </Link>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              <Dialog open={isLoginOpen} onOpenChange={setIsLoginOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="hidden sm:inline-flex text-white hover:bg-white/10">
                    Sign In
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[hsl(224,71%,6%)] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle>Sign In to ServSpect</DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                      Access your server monitoring dashboard
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSignIn} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input
                        id="signin-email"
                        name="email"
                        type="email"
                        placeholder="you@company.com"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <Input
                        id="signin-password"
                        name="password"
                        type="password"
                        className="bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                    {error && (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading}>
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={isSignUpOpen} onOpenChange={setIsSignUpOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                    Get Started
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[hsl(224,71%,6%)] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle>Create Your Account</DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                      Start monitoring your servers for free
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSignUp} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full Name</Label>
                      <Input
                        id="signup-name"
                        name="fullName"
                        type="text"
                        placeholder="John Doe"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-tenant">Organization Name</Label>
                      <Input
                        id="signup-tenant"
                        name="tenantName"
                        type="text"
                        placeholder="Acme Corp"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        name="email"
                        type="email"
                        placeholder="you@company.com"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <Input
                        id="signup-password"
                        name="password"
                        type="password"
                        className="bg-white/5 border-white/10 text-white"
                        required
                      />
                    </div>
                    <Alert className="bg-primary/20 border-primary/30">
                      <AlertTriangle className="h-4 w-4 text-primary" />
                      <AlertDescription className="text-primary">
                        Account registration is currently disabled. Please check back soon!
                      </AlertDescription>
                    </Alert>
                    <Button type="button" className="w-full bg-primary/50 cursor-not-allowed" disabled>
                      Coming Soon
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Mobile Menu Button */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </nav>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
              <div className="flex flex-col gap-2">
                <Link to="/install" className="px-4 py-2 hover:bg-white/10 rounded-md">Install</Link>
                <Link to="/knowledge-hub" className="px-4 py-2 hover:bg-white/10 rounded-md">Knowledge Hub</Link>
                <Link to="/demo" className="px-4 py-2 hover:bg-white/10 rounded-md">Demo</Link>
                <Link to="/privacy" className="px-4 py-2 hover:bg-white/10 rounded-md">Privacy</Link>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
        
        <div className="container mx-auto px-4 py-20 md:py-32 relative">
          <div className="text-center max-w-4xl mx-auto">
            {/* New Feature Badge */}
            <Link 
              to="/install" 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors mb-8"
            >
              <Badge className="bg-primary text-primary-foreground text-xs">New</Badge>
              <span className="text-sm">Token-free HMAC Authentication</span>
              <ArrowRight className="w-4 h-4" />
            </Link>

            {/* Main Title */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Simple and Secure</span>
              <br />
              <span className="text-primary">Server Monitoring</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-white/70 mb-10 max-w-3xl mx-auto leading-relaxed">
              ServSpect combines enterprise-grade monitoring with Zero Trust security, 
              providing a unified platform for Windows and Linux server performance metrics, 
              unified log management, compliance tracking, and intelligent alerting.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg"
                onClick={() => setIsSignUpOpen(true)}
              >
                Get started - free
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 bg-transparent text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => navigate("/demo")}
              >
                <Play className="w-5 h-5 mr-2 fill-white" />
                <span>View Demo</span>
              </Button>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-8">
              {features.map((feature, index) => (
                <button
                  key={index}
                  onClick={() => setActiveFeature(index)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    activeFeature === index 
                      ? 'bg-primary/20 border-b-2 border-primary' 
                      : 'hover:bg-white/5'
                  }`}
                >
                  <feature.icon className={`w-5 h-5 ${activeFeature === index ? 'text-primary' : 'text-white/60'}`} />
                  <div className="text-left">
                    <div className={`text-sm font-medium ${activeFeature === index ? 'text-primary' : 'text-white/60'}`}>
                      {feature.title}
                    </div>
                    <div className={`text-xs ${activeFeature === index ? 'text-primary/80' : 'text-white/40'}`}>
                      {feature.subtitle}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Integration Logos */}
        <div className="border-y border-white/10 py-8">
          <div className="container mx-auto px-4">
            <p className="text-center text-white/40 text-sm mb-6">Integrates with</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
              <div className="flex items-center gap-2 text-white/60">
                <Server className="w-6 h-6" />
                <span>Windows Server</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Terminal className="w-6 h-6" />
                <span>Linux</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Shield className="w-6 h-6" />
                <span>Active Directory</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Bell className="w-6 h-6" />
                <span>Slack</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Users className="w-6 h-6" />
                <span>Microsoft Teams</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Globe className="w-6 h-6" />
                <span>Email SMTP</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      {featureSections.map((section, index) => (
        <section 
          key={index}
          id={section.id}
          className={`py-20 ${index % 2 === 0 ? 'bg-[hsl(224,71%,4%)]' : 'bg-[hsl(224,71%,6%)]'}`}
        >
          <div className="container mx-auto px-4">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">
                  {section.title}
                </h2>
                <p className="text-lg text-white/70 mb-8">
                  {section.subtitle}
                </p>
                <ul className="space-y-4">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-primary/20 text-primary mt-0.5">
                        <item.icon className="w-5 h-5" />
                      </div>
                      <span className="text-white/80">{item.text}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="mt-8 bg-primary hover:bg-primary/90"
                  onClick={() => setIsSignUpOpen(true)}
                >
                  Explore <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
                  <img 
                    src={section.image} 
                    alt={section.title}
                    className="w-full h-auto"
                  />
                  {/* Overlay with mock UI elements */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(224,71%,4%)] via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Testimonials Section */}
      <section className="py-20 bg-[hsl(224,71%,6%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            ServSpect powers teams around the world
          </h2>
          <p className="text-center text-white/60 mb-12">
            Trusted by IT teams to monitor their critical infrastructure
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "ServSpect transformed our server monitoring. The zero-config agent deployment saved us hours of setup time, and the security features give us peace of mind.",
                author: "IT Operations Manager",
                company: "Enterprise Tech Co"
              },
              {
                quote: "The Windows event log analysis is incredibly powerful. We catch security incidents before they become problems, and the alerting system is exactly what we needed.",
                author: "Security Engineer",
                company: "Financial Services"
              },
              {
                quote: "As an MSP, we needed multi-tenant monitoring that scales. ServSpect delivers enterprise features at a price point that makes sense for our business.",
                author: "Managed Services Director",
                company: "IT Solutions Provider"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white/5 rounded-xl p-6 border border-white/10">
                <p className="text-white/80 mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <div className="font-medium text-white">{testimonial.author}</div>
                  <div className="text-sm text-white/60">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-[hsl(224,71%,4%)] to-primary/20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to secure your servers?
          </h2>
          <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
            Get started in minutes with our zero-config agent. 
            No credit card required for the free tier.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg"
              onClick={() => setIsSignUpOpen(true)}
            >
              Get started - free
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
              onClick={() => navigate("/install")}
            >
              View Installation Guide
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 bg-[hsl(224,71%,4%)]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary rounded-lg">
                  <Shield className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="text-lg font-bold">ServSpect</span>
              </div>
              <p className="text-white/60 text-sm max-w-md">
                Enterprise-grade server monitoring and security platform. 
                Monitor performance, manage unified logs, track compliance, and protect your Windows and Linux infrastructure.
              </p>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/install" className="hover:text-white transition-colors">Installation</Link></li>
                <li><Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link></li>
                <li><Link to="/api-docs" className="hover:text-white transition-colors">API Documentation</Link></li>
                <li><Link to="/knowledge-hub" className="hover:text-white transition-colors">Knowledge Hub</Link></li>
                <li><Link to="/demo" className="hover:text-white transition-colors">Demo</Link></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/security" className="hover:text-white transition-colors">Security</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60">
              © {new Date().getFullYear()} ServSpect. All rights reserved.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-white/60 hover:text-white transition-colors">
                <Github className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
